import { createContext, useContext, useMemo, type ReactElement, type ReactNode } from "react";

interface EntitlementsValue {
  appAccess: readonly string[];
  tabAccess: readonly string[];
  hasAppAccess: (appId: string) => boolean;
  hasTabAccess: (tabId: string) => boolean;
  hasSandboxAccess: boolean;
}

export const WORKSPACE_ACCESS_KEY = "workspace.core";
export const SANDBOX_ACCESS_PREFIX = "sandbox.";
export const EXPLICIT_NATIVE_APP_ACCESS = new Set(["brainstorming", "sentiment-workbench"]);
const NATIVE_APP_ACCESS_KEYS: Readonly<Record<string, string>> = {
  "sentiment-workbench": "sandbox.sentiment_workbench",
  sentiment_workbench: "sandbox.sentiment_workbench",
};

const DENIED: EntitlementsValue = {
  appAccess: [],
  tabAccess: [],
  hasAppAccess: () => false,
  hasTabAccess: () => false,
  hasSandboxAccess: false,
};

const EntitlementsContext = createContext<EntitlementsValue>(DENIED);

function permits(keys: readonly string[], key: string): boolean {
  return keys.includes("*") || keys.includes(key);
}

function permitsApp(keys: readonly string[], key: string): boolean {
  // Preserve existing user grants through the native migration, including saved legacy IDs.
  const nativeAccessKey = NATIVE_APP_ACCESS_KEYS[key];
  if (nativeAccessKey) return permits(keys, nativeAccessKey);
  // Brainstorming is an explicit UAT pilot: it stays hidden and guarded until a SuperAdmin grants
  // the app key. Other native apps retain the existing Workspace-product entitlement behavior.
  if (EXPLICIT_NATIVE_APP_ACCESS.has(key)) return permits(keys, key);
  if (key.startsWith(SANDBOX_ACCESS_PREFIX)) return permits(keys, key);
  return permits(keys, WORKSPACE_ACCESS_KEY) || permits(keys, key);
}

export function EntitlementsProvider({
  appAccess,
  tabAccess,
  children,
}: {
  appAccess: readonly string[];
  tabAccess: readonly string[];
  children: ReactNode;
}): ReactElement {
  const value = useMemo<EntitlementsValue>(() => ({
    appAccess,
    tabAccess,
    hasAppAccess: (appId) => permitsApp(appAccess, appId),
    hasTabAccess: (tabId) => permits(tabAccess, tabId),
    hasSandboxAccess: appAccess.includes("*")
      || appAccess.includes(WORKSPACE_ACCESS_KEY)
      || appAccess.some((key) => key.startsWith(SANDBOX_ACCESS_PREFIX)),
  }), [appAccess, tabAccess]);
  return <EntitlementsContext.Provider value={value}>{children}</EntitlementsContext.Provider>;
}

export function useEntitlements(): EntitlementsValue {
  return useContext(EntitlementsContext);
}
